import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Highlighter from 'react-highlight-words';
import {
  ChevronDown as ExpandToggleIcon,
  FileText as SurveyIcon,
  Users as CustomerIcon,
  X as CloseIcon
} from 'lucide-react';

import { paths } from 'configuration/paths';
import { classNameList } from 'utils/property';
import { getAvailableSortedSurveyDefs } from 'utils/survey';
import { TranslationsKeys } from 'types/translation';
import { CustomerVisit } from 'types/customer';
import { Survey } from 'types/survey';
import { useAuthentication } from 'hooks/useAuthentication';
import { useTranslate } from 'hooks/useTranslate';
import { useStartSurvey } from 'hooks/useStartSurvey';
import { useCustomerVisitsQuery, useSurveyDefinitionsQuery, useSurveyResponseQuery } from 'hooks/queries';
import { useUpdateSurveyResponse } from 'hooks/mutations';
import { useSurveyTranslation } from 'hooks/useSurveyTranslation';
import { SurveyStepper, SurveyStepperSteps } from 'components/SurveyStepper';
import { Header } from 'components/Header';
import { Footer } from 'components/Footer';
import { Spinner } from 'components/Spinner';
import { Button } from 'components/ui/Button';
import { MessageBar } from 'components/MessageBar';
import { FullscreenContainer } from 'components/FullscreenContainer';
import { SearchInput } from 'components/ui/SearchInput';
import { Checkbox } from 'components/ui/Checkbox';
import { Modal } from 'components/Modal';
import { getHighlighterProps } from './util';
import { SurveyDefinitionListEntry } from './components/SurveyDefinitionListEntry';
import styles from './SurveyIntro.module.scss';

const SurveyIntro = (): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { user } = useAuthentication();
  const { visitId, surveyId } = useParams();
  const startSurvey = useStartSurvey();
  const customerVisitsQuery = useCustomerVisitsQuery(user?.email ?? ''); // should not be empty
  const [plannedVisits, setPlannedVisits] = useState<CustomerVisit[]>([]);
  const [visibleVisits, setVisibleVisits] = useState<CustomerVisit[]>([]);
  const [surveyTemplates, setSurveyTemplates] = useState<Survey[]>([]);
  const [visibleSurveyTemplates, setVisibleSurveyTemplates] = useState<Survey[]>([]);
  const [selectedVisit, setSelectedVisit] = useState<CustomerVisit | undefined>();
  const [survey, setSurvey] = useState<Survey | undefined>();
  const [showCustomerVisitSelection, setShowCustomerVisitSelection] = useState<boolean>(false);
  const [showSurveySelection, setShowSurveySelection] = useState<boolean>(false);
  const [collapseCustomerInformation, setCollapseCustomerInformation] = useState<boolean>(true);
  const [expandedPlannedVisits, setExpandedPlannedVisits] = useState<string[]>([]);
  const [canStartSurvey, setCanStartSurvey] = useState<boolean>(false);
  const [customerQuery, setCustomerQuery] = useState<string | undefined>();
  const [surveyQuery, setSurveyQuery] = useState<string | undefined>();
  const [surveyFilters, setSurveyFilters] = useState<string[]>([]);
  const [showSurveyFilters, setShowSurveyFilters] = useState<boolean>(false);
  const [showSurveyConfirmation, setShowSurveyConfirmation] = useState<boolean>(false);
  const surveyDefinitionQuery = useSurveyDefinitionsQuery();
  const surveyResponseQuery = useSurveyResponseQuery(surveyId);
  const updateSurveyResponse = useUpdateSurveyResponse();
  const isExistingSurveyMode = !!surveyId, surveyResponse = surveyResponseQuery.data;
  const ts = useSurveyTranslation(survey);
  
  const surveyDefinitions = useMemo(() => 
    surveyDefinitionQuery.data
      ? getAvailableSortedSurveyDefs(surveyDefinitionQuery.data, user?.locale)
      : [], [surveyDefinitionQuery.data, user?.locale]);

  useEffect(() => {
    if (visitId && customerVisitsQuery.data) {
      const visit = customerVisitsQuery.data.find(cv => cv.plannedVisit.visitId === visitId);
      if (visit) {
        setSelectedVisit(visit);
      }
    }
  }, [visitId, customerVisitsQuery.data]);

  useEffect(() => {
    if (surveyResponse && surveyDefinitionQuery.data) {
      setSurvey(surveyDefinitionQuery.data.find(sd => sd.surveyDefinitionId === surveyResponse.surveyDefinitionId));
      if (surveyResponse.visit && surveyResponse.customer) {
        setSelectedVisit({ plannedVisit: surveyResponse.visit, customer: surveyResponse.customer });
      }
    }
  }, [surveyResponse, surveyDefinitionQuery.data]);

  const filterCustomers = (query: string) => {
    setCustomerQuery(query);
    setVisibleVisits(
      plannedVisits.filter((visit) => {
        return (
          visit.customer.companyName.toLowerCase().includes(query.toLowerCase()) ||
          visit.customer.contactName.toLowerCase().includes(query.toLowerCase()) ||
          visit.customer.address.toLowerCase().includes(query.toLowerCase()) ||
          visit.customer.sector.toLowerCase().includes(query.toLowerCase()) ||
          visit.customer.businessAreaCode?.toLowerCase().includes(query.toLowerCase()) ||
          visit.customer.customerId.toLowerCase().includes(query.toLowerCase()) ||
          visit.plannedVisit.startDate.toLowerCase().includes(query.toLowerCase()) ||
          visit.plannedVisit.visitTypeCode.toLowerCase().includes(query.toLowerCase()) ||
          visit.plannedVisit.attendees.find((attendee) =>
            attendee.name?.toLowerCase().includes(query.toLowerCase())
          ) !== undefined
        );
      })
    );
  };

  const togglePlannedVisit = (visit: CustomerVisit) => {
    if (!customerQuery) {
      setExpandedPlannedVisits((prev) =>
        prev.includes(visit.plannedVisit.visitId)
          ? prev.filter((id) => id !== visit.plannedVisit.visitId)
          : [...prev, visit.plannedVisit.visitId]
      );
    }
  };

  const filterSurveys = (query: string) => {
    setSurveyQuery(query);

    setVisibleSurveyTemplates(
      surveyTemplates.filter((survey) => {
        return (
          survey.name.toLowerCase().includes(query.toLowerCase()) ||
          ts(survey.sector).toLowerCase().includes(query.toLowerCase())
        );
      })
    );
  };

  const getUniqueSurveyTemplateFilters = () => {
    if (visibleSurveyTemplates.length < 1) {
      return [];
    }

    return visibleSurveyTemplates
      .filter((template, i, data) => data.findIndex((innerTemplate) => innerTemplate.sector === template.sector) === i)
      .filter(
        (template) =>
          template.sector !== undefined && (surveyFilters.length > 0 ? surveyFilters.includes(template.sector) : true)
      );
  };

  const toggleSurveyFilter = (surveyfilter: string) => {
    surveyFilters.includes(surveyfilter)
      ? setSurveyFilters([...surveyFilters.filter((filter) => filter !== surveyfilter)])
      : setSurveyFilters(surveyFilters.concat(surveyfilter));
  };

  const closeSearchWindow = () => {
    setShowCustomerVisitSelection(false);
    setShowSurveySelection(false);
    setCustomerQuery(undefined);
    setSurveyQuery(undefined);
    setVisibleVisits(plannedVisits);
    setVisibleSurveyTemplates(surveyTemplates);
    setExpandedPlannedVisits([]);
  };

  useEffect(() => {
    setVisibleVisits(plannedVisits);
    setSurveyTemplates(surveyDefinitions);
    setVisibleSurveyTemplates(surveyDefinitions);
    setCanStartSurvey(false);
  }, [surveyDefinitions]);

  useEffect(() => {
    if (customerVisitsQuery.data) {
      setPlannedVisits(customerVisitsQuery.data);
      setSurveyQuery(undefined);
      setVisibleVisits(customerVisitsQuery.data);
    }
  }, [customerVisitsQuery.data]);

  const doStartSurvey = async (forceWithoutCustomer?: boolean) => {
    if (!survey) {
      return;
    }

    if (typeof selectedVisit === 'undefined' && !forceWithoutCustomer) {
      setShowSurveyConfirmation(true);
      return;
    }

    const surveyResponse = await startSurvey(
      survey.surveyDefinitionId,
      survey.version,
      selectedVisit?.customer?.customerId,
      selectedVisit?.customer?.companyName,
      selectedVisit?.customer,
      selectedVisit?.plannedVisit
    );

    navigate('/survey/' + surveyResponse.responseId);
  };

  const doUpdateSurvey = async () => {
    if (!surveyResponse) {
      return;
    }

    if (selectedVisit?.plannedVisit !== surveyResponse.visit) {
      await updateSurveyResponse.mutateAsync({
        ...surveyResponse,
        visit: selectedVisit?.plannedVisit,
        customer: selectedVisit?.customer,
        customerId: selectedVisit?.customer?.customerId,
        customerName: selectedVisit?.customer?.companyName,
      });
    }

    navigate('/survey/' + surveyResponse.responseId);
  };

  useEffect(() => {
    setCollapseCustomerInformation(true);
  }, [showCustomerVisitSelection]);

  useEffect(() => {
    setVisibleSurveyTemplates(surveyDefinitions);
  }, [showSurveySelection]);

  useEffect(() => {
    if (surveyFilters.length === 0) {
      setVisibleSurveyTemplates(surveyTemplates);
      return;
    }

    setVisibleSurveyTemplates(
      surveyTemplates.filter((survey) => {
        return surveyFilters.some(
          (filter) =>
            survey.name.toLowerCase().includes(filter.toLowerCase()) ||
            ts(survey.sector).toLowerCase().includes(filter.toLowerCase())
        );
      })
    );
  }, [surveyFilters]);

  return (
    <>
      <Header />
      <SurveyStepper
        activeStep={SurveyStepperSteps.Intro} 
        surveyResponse={surveyResponse}
      />
      <div className={styles.surveyIntro}>
        <div className={styles.surveyIntroHeading}>{t(TranslationsKeys.surveyIntroPage_title)}</div>
        {!selectedVisit && (
          <div className={styles.surveyIntroNoCustomer}>{t(TranslationsKeys.surveyIntroPage_noCustomerText)}</div>
        )}
        <div className={styles.surveyIntroSelections}>
          <div
            className={classNameList(
              styles.surveyIntroSelectionsCustomer,
              selectedVisit && styles.surveyIntroSelectionsCustomerSelected
            )}>
            <div className={styles.surveyIntroSelectionsCustomerIconNameContainer}>
              <div className={styles.surveyIntroSelectionsCustomerIcon}>
                <CustomerIcon />
              </div>
              <div className={styles.surveyIntroSelectionsCustomerNameContainer}>
                <span>{t(TranslationsKeys.common_customer)}:</span>
                {selectedVisit && <span>{selectedVisit.customer.companyName}</span>}
                {!selectedVisit ? (
                  <Button
                    variant="primary"
                    size="small"
                    aria-label={t(TranslationsKeys.surveyIntroPage_openCustomerSelectionAriaLabel)}
                    onClick={() => setShowCustomerVisitSelection(true)}>
                    {t(TranslationsKeys.common_select)}
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="small"
                    aria-label={t(TranslationsKeys.surveyIntroPage_openCustomerSelectionAriaLabel)}
                    onClick={() => setShowCustomerVisitSelection(true)}>
                    {t(TranslationsKeys.common_edit)}
                  </Button>
                )}
              </div>
            </div>
            {selectedVisit && (
              <div className={styles.surveyIntroSelectionsCustomerInformation}>
                <div className={styles.surveyIntroSelectionsCustomerInformationRow}>
                  <div>{t(TranslationsKeys.common_plannedVisit)}:</div>
                  <div>{selectedVisit.plannedVisit.startDate}</div>
                </div>
                <div className={styles.surveyIntroSelectionsCustomerInformationRow}>
                  <div>{t(TranslationsKeys.common_address)}:</div>
                  <div>{selectedVisit.customer.address}</div>
                </div>
                <div
                  className={classNameList(
                    styles.surveyIntroSelectionsCustomerInformationCollapsible,
                    collapseCustomerInformation && styles.surveyIntroSelectionsCustomerInformationCollapsibleCollapsed
                  )}>
                  <div className={styles.surveyIntroSelectionsCustomerInformationRow}>
                    <div>{t(TranslationsKeys.common_contact)}:</div>
                    <div>{selectedVisit.customer.contactName}</div>
                  </div>
                  <div className={styles.surveyIntroSelectionsCustomerInformationRow}>
                    <div>{t(TranslationsKeys.common_business)}:</div>
                    <div>{selectedVisit.customer.businessAreaCode}</div>
                  </div>
                  <div className={styles.surveyIntroSelectionsCustomerInformationRow}>
                    <div>{t(TranslationsKeys.common_customerNumberAbbreviated)}:</div>
                    <div>{selectedVisit.customer.customerId}</div>
                  </div>
                  <div className={styles.surveyIntroSelectionsCustomerInformationRow}>
                    <div>{t(TranslationsKeys.surveyIntroPage_customerVisit_visitType)}:</div>
                    <div>{selectedVisit.plannedVisit.visitTypeCode}</div>
                  </div>
                  <div className={styles.surveyIntroSelectionsCustomerInformationRow}>
                    <div>{t(TranslationsKeys.surveyIntroPage_customerVisit_participants)}:</div>
                    <div>
                      {selectedVisit.plannedVisit.attendees.map((attendee, index) => (
                        <span key={attendee.email}>
                          {attendee.name}
                          {index < selectedVisit.plannedVisit.attendees.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className={styles.surveyIntroSelectionsCustomerInformationExpandToggle}>
                  <button onClick={() => setCollapseCustomerInformation(!collapseCustomerInformation)}>
                    {collapseCustomerInformation
                      ? t(TranslationsKeys.common_showMore)
                      : t(TranslationsKeys.common_showLess)}
                    <ExpandToggleIcon
                      className={classNameList(
                        styles.surveyIntroSelectionsCustomerInformationExpandToggleIcon,
                        collapseCustomerInformation &&
                          styles.surveyIntroSelectionsCustomerInformationExpandToggleIconCollapsed
                      )}
                    />
                  </button>
                </div>
              </div>
            )}
          </div>
          <div
            className={classNameList(
              styles.surveyIntroSelectionsSurvey,
              survey && styles.surveyIntroSelectionsSurveySelected
            )}>
            <div className={styles.surveyIntroSelectionsSurveyIconNameContainer}>
              <div className={styles.surveyIntroSelectionsSurveyIcon}>
                <SurveyIcon />
              </div>
              <div className={styles.surveyIntroSelectionsSurveyNameContainer}>
                <span>{t(TranslationsKeys.common_survey)}:</span>
                {survey && <span className={styles.surveyIntroSelectionsSurveyName}>{ts(survey.name)}</span>}
                {!isExistingSurveyMode && (
                  !survey ? (
                    <Button
                      variant="primary"
                      size="small"
                      aria-label={t(TranslationsKeys.surveyIntroPage_openSurveyTemplateSelectionAriaLabel)}
                      onClick={() => setShowSurveySelection(true)}>
                      {t(TranslationsKeys.common_select)}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="small"
                      aria-label={t(TranslationsKeys.surveyIntroPage_openSurveyTemplateSelectionAriaLabel)}
                      onClick={() => setShowSurveySelection(true)}>
                      {t(TranslationsKeys.common_edit)}
                    </Button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
        <Footer className={styles.surveyIntroFooter}>
          <div>
            <Button
              variant="secondary"
              onClick={() => {
                navigate(paths.mySurveys);
              }}>
              {t(isExistingSurveyMode ? TranslationsKeys.surveyWrapUpPage_saveAsDraft : TranslationsKeys.common_cancel)}
            </Button>
          </div>
          <div>
            {isExistingSurveyMode
              ? <Button
                className={styles.mySurveysFooterButton}
                state={updateSurveyResponse.isPending ? 'disabled' : 'normal'}
                onClick={() => doUpdateSurvey()}>
                {t(TranslationsKeys.surveyPage_continue)}
              </Button>
              : <Button
                className={styles.mySurveysFooterButton}
                state={canStartSurvey ? 'normal' : 'disabled'}
                onClick={() => doStartSurvey()}>
                {t(TranslationsKeys.mySurveysPage_newSurveyButtonText)}
              </Button>}
          </div>
        </Footer>
        
        {showCustomerVisitSelection && (
          <FullscreenContainer>
            <div className={styles.surveyIntroCustomerSearch}>
              <div className={styles.surveyIntroCustomerSearchFixedHeader}>
                <div className={styles.surveyIntroCustomerSearchHeading}>
                  <span>{t(TranslationsKeys.surveyIntroPage_selectCustomerHeading)}</span>
                  <button onClick={closeSearchWindow}>
                    <CloseIcon />
                  </button>
                </div>
                <div className={styles.surveyIntroCustomerSearchForm}>
                  <SearchInput variant="inline" onChange={filterCustomers} />
                  <div className={styles.surveyIntroCustomerSearchFormResultCount}>
                    {!customerQuery
                      ? t(TranslationsKeys.common_all)
                      : `(${visibleVisits.length}) ${t(TranslationsKeys.common_results).toLowerCase()}`}
                  </div>
                </div>
              </div>
              <div className={styles.surveyIntroCustomerSearchMessageBarContainer}>
                <MessageBar
                  title={t(TranslationsKeys.surveyIntroPage_selectCustomerMessageBarTitle)}
                  description={t(TranslationsKeys.surveyIntroPage_selectCustomerMessageBarDescription)}
                  variant="info"
                  showCloseAction={false}
                />
              </div>
              <div className={styles.surveyIntroCustomerSearchPlannedVisits}>
                {customerVisitsQuery.isLoading && !customerVisitsQuery.data && <Spinner />}
                {visibleVisits.map((visit) => (
                  <div
                    key={visit.plannedVisit.visitId}
                    className={styles.surveyIntroCustomerSearchPlannedVisitsCustomer}>
                    <div className={styles.surveyIntroCustomerSearchPlannedVisitsCustomerName}>
                      <div>
                        <Highlighter {...getHighlighterProps(visit.customer.companyName, customerQuery)} />
                      </div>
                      <div>
                        <Button
                          variant="primary"
                          size="small"
                          aria-label={t(TranslationsKeys.surveyIntroPage_noCustomerConfirmationAddCustomerButtonText)}
                          onClick={() => {
                            setSelectedVisit(visit);
                            setCanStartSurvey(typeof survey !== 'undefined');
                            closeSearchWindow();
                          }}>
                          {t(TranslationsKeys.common_select)}
                        </Button>
                      </div>
                    </div>
                    <div className={styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfo}>
                      <div className={styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfoRow}>
                        <div>{t(TranslationsKeys.common_plannedVisit)}:</div>
                        <div>
                          <Highlighter {...getHighlighterProps(visit.plannedVisit.startDate, customerQuery)} />
                        </div>
                      </div>
                      <div className={styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfoRow}>
                        <div>{t(TranslationsKeys.common_address)}:</div>
                        <div>
                          <Highlighter {...getHighlighterProps(visit.customer.address, customerQuery)} />
                        </div>
                      </div>
                      <div
                        className={classNameList(
                          styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfoCollapsible,
                          !expandedPlannedVisits.includes(visit.plannedVisit.visitId) &&
                            !customerQuery &&
                            styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfoCollapsibleCollapsed
                        )}>
                        <div className={styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfoRow}>
                          <div>{t(TranslationsKeys.common_sector)}:</div>
                          <div>
                            <Highlighter {...getHighlighterProps(visit.customer.sector, customerQuery)} />
                          </div>
                        </div>
                        <div className={styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfoRow}>
                          <div>{t(TranslationsKeys.common_business)}:</div>
                          <div>
                            <Highlighter
                              {...getHighlighterProps(visit.customer.businessAreaCode ?? '', customerQuery)}
                            />
                          </div>
                        </div>
                        <div className={styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfoRow}>
                          <div>{t(TranslationsKeys.common_customerNumberAbbreviated)}:</div>
                          <div>
                            <Highlighter {...getHighlighterProps(visit.customer.customerId, customerQuery)} />
                          </div>
                        </div>
                      </div>
                      <div
                        className={classNameList(
                          styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfoExpandToggle,
                          customerQuery && styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfoExpandToggleDisabled
                        )}>
                        <button onClick={() => togglePlannedVisit(visit)}>
                          {!expandedPlannedVisits.includes(visit.plannedVisit.visitId) && !customerQuery
                            ? t(TranslationsKeys.common_showMore)
                            : t(TranslationsKeys.common_showLess)}
                          <ExpandToggleIcon
                            className={classNameList(
                              styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfoExpandToggleIcon,
                              !expandedPlannedVisits.includes(visit.plannedVisit.visitId) &&
                                !customerQuery &&
                                styles.surveyIntroCustomerSearchPlannedVisitsCustomerInfoExpandToggleIconCollapsed
                            )}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </FullscreenContainer>
        )}
        
        {showSurveySelection && (
          <FullscreenContainer>
            <div className={styles.surveyIntroSurveySearch}>
              <div
                className={classNameList(
                  styles.surveyIntroSurveySearchFixedHeader,
                  showSurveyFilters && styles.surveyIntroSurveySearchFixedHeaderOverlay
                )}>
                <div className={styles.surveyIntroSurveySearchHeading}>
                  <span>{t(TranslationsKeys.surveyIntroPage_selectSurveyTemplateHeading)}</span>
                  <button onClick={closeSearchWindow}>
                    <CloseIcon />
                  </button>
                </div>
                <div className={styles.surveyIntroSurveySearchForm}>
                  <SearchInput variant="inline" onChange={filterSurveys} />
                  <div className={styles.surveyIntroSurveySearchFormResultCount}>
                    {!surveyQuery
                      ? t(TranslationsKeys.common_all)
                      : `(${visibleSurveyTemplates.length}) ${t(TranslationsKeys.common_results).toLowerCase()}`}
                  </div>
                </div>
                <div
                  className={classNameList(
                    styles.surveyIntroSurveySearchFilters,
                    showSurveyFilters && styles.surveyIntroSurveySearchFiltersOpen
                  )}>
                  <button
                    className={styles.surveyIntroSurveySearchFiltersHeading}
                    onClick={() => setShowSurveyFilters(!showSurveyFilters)}>
                    <div>
                      {t(TranslationsKeys.common_filters)} ({surveyFilters.length})
                    </div>
                    <div>
                      <ExpandToggleIcon />
                    </div>
                  </button>
                  <div
                    className={classNameList(
                      styles.surveyIntroSurveySearchFiltersList,
                      !showSurveyFilters && styles.surveyIntroSurveySearchFiltersListCollapsed
                    )}>
                    <div>
                      <button
                        className={styles.surveyIntroSurveySearchFiltersListClearFilters}
                        disabled={surveyFilters.length === 0}
                        onClick={() => {
                          setSurveyFilters([]);
                        }}>
                        {t(TranslationsKeys.common_clearAll)}
                      </button>
                    </div>
                    <div className={styles.surveyIntroSurveySearchFiltersListCheckboxes}>
                      {getUniqueSurveyTemplateFilters()?.map((template) => (
                        <div key={template.sector} className={styles.surveyIntroSurveySearchFiltersListItem}>
                          <Checkbox
                            label={ts(template.sector)}
                            onClick={() => toggleSurveyFilter(template.sector)}
                            checked={surveyFilters.includes(template.sector)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.surveyIntroSurveySearchSurveyTemplates}>
                {surveyDefinitionQuery.isLoading && (
                  <Spinner size="large" className={styles.surveyIntroSurveySearchSurveyTemplatesSpinner} />
                )}
                {surveyDefinitionQuery.isError && (
                  <div className="text-danger text-center">{t(TranslationsKeys.common_loadingError)}</div>
                )}
                {visibleSurveyTemplates.map((survey) => (
                  <SurveyDefinitionListEntry
                    key={survey.surveyDefinitionId}
                    survey={survey}
                    surveyQuery={surveyQuery ?? ''}
                    onClick={() => {
                      setSurvey(survey);
                      setCanStartSurvey(true);
                      closeSearchWindow();
                    }}
                  />
                ))}
              </div>
            </div>
          </FullscreenContainer>
        )}
      </div>
      {showSurveyConfirmation && (
        <Modal
          title={t(TranslationsKeys.surveyIntroPage_noCustomerConfirmationTitle)}
          description={t(TranslationsKeys.surveyIntroPage_noCustomerConfirmationDescription)}
          primaryButtonLabel={t(TranslationsKeys.surveyIntroPage_noCustomerConfirmationContinueButtonText)}
          secondaryButtonLabel={t(TranslationsKeys.surveyIntroPage_noCustomerConfirmationAddCustomerButtonText)}
          onPrimaryClick={() => doStartSurvey(true)}
          onSecondaryClick={() => {
            setShowSurveyConfirmation(false);
            setShowCustomerVisitSelection(true);
          }}
          onCancel={() => setShowSurveyConfirmation(false)}
        />
      )}
    </>
  );
};

export default SurveyIntro;
